/**
 * UK statutory holiday entitlement helpers (GB).
 *
 * - Statutory entitlement is 5.6 weeks, capped at 28 days for a 5+ day week.
 * - Part-time regular workers: pro-rata (5.6 x days per week), expressed here in hours.
 * - Irregular-hours / part-year workers (zero-hours, casual) for leave years starting on
 *   or after 1 April 2024: accrue 12.07% of hours worked in each pay period.
 * - Starters/leavers part way through the leave year: pro-rated by days employed.
 * - First year of employment: leave accrues at 1/12 of the annual amount at the start
 *   of each month (Reg 15A), with day fractions rounded to half/whole days.
 * Bank holidays are treated as part of the 5.6 weeks (common practice); companies
 * that give bank holidays on top should add them as an adjustment or override.
 * This is planning arithmetic, not legal or payroll advice.
 */
import { DateTime } from 'luxon';
import type { EmploymentType } from './types';

export const STATUTORY_WEEKS = 5.6;
export const STATUTORY_MAX_DAYS = 28;
export const IRREGULAR_ACCRUAL_RATE = 0.1207;
const IRREGULAR_ACCRUAL_RATE_DEFAULT = IRREGULAR_ACCRUAL_RATE;

/** How a contract gets holiday (Time Off): see HOLIDAY_METHODS. */
export type HolidayMethod = 'rolled_up' | 'accrued' | 'fixed';
export const HOLIDAY_METHODS: { id: HolidayMethod; label: string; help: string }[] = [
  { id: 'rolled_up', label: 'Rolled-up holiday pay (paid with wages)', help: 'A percentage (usually 12.07%) is added to each payslip, so there is no paid allowance to book.' },
  { id: 'accrued', label: 'Accrued allowance (12.07% of hours accrues as bookable leave)', help: 'Each hour worked earns bookable leave at the rate, from approved timesheets.' },
  { id: 'fixed', label: 'Fixed annual allowance', help: 'A set amount each holiday year (default 5.6 weeks pro-rata to weekly hours), pro-rated for starters and leavers.' },
];
export const DEFAULT_HOLIDAY_RATE = IRREGULAR_ACCRUAL_RATE_DEFAULT;

export interface EntitlementInput {
  employment_type: EmploymentType;
  holiday_method?: HolidayMethod | null;
  /** Rolled-up pay or accrual rate, e.g. 0.1207. */
  holiday_rate?: number | null;
  /** Fixed allowance amount; NULL = 5.6 weeks pro-rata to contracted hours. */
  holiday_allowance?: number | null;
  holiday_allowance_unit?: 'days' | 'hours' | null;
  contracted_hours_per_week: number | null;
  contracted_days_per_week: number | null;
  employment_start_date?: string | null;
  employment_end_date?: string | null;
  entitlement_override_hours?: number | null;
}

export interface LeaveYear {
  start: string;
  end: string; // inclusive
}

const round2 = (n: number) => Math.round(n * 100) / 100;

export function isIrregular(type: EmploymentType): boolean {
  return type === 'zero_hours' || type === 'casual';
}

export function leaveYearFor(date: string, startMonth = 1, startDay = 1): LeaveYear {
  const d = DateTime.fromISO(date);
  let start = DateTime.fromObject({ year: d.year, month: startMonth, day: startDay });
  if (start > d) start = start.minus({ years: 1 });
  return { start: start.toISODate()!, end: start.plus({ years: 1 }).minus({ days: 1 }).toISODate()! };
}

export function hoursPerDay(input: EntitlementInput): number | null {
  const h = input.contracted_hours_per_week;
  const d = input.contracted_days_per_week;
  if (!h || !d) return null;
  return h / d;
}

/** Hours per day for converting days: the contract's pattern, else weekly hours / 5. */
export function dayHours(input: EntitlementInput): number | null {
  return hoursPerDay(input) ?? (input.contracted_hours_per_week ? input.contracted_hours_per_week / 5 : null);
}

/** A fixed allowance in hours: the set amount (days converted at hours per day), else 5.6 weeks pro-rata. */
export function fixedAllowanceHours(input: EntitlementInput): number {
  if (input.holiday_allowance != null) {
    if ((input.holiday_allowance_unit ?? 'days') === 'hours') return round2(input.holiday_allowance);
    const hpd = dayHours(input);
    return hpd ? round2(input.holiday_allowance * hpd) : 0;
  }
  return fullYearEntitlementHours({ ...input, entitlement_override_hours: null });
}

/** Full-year statutory entitlement in hours for a regular-hours worker. */
export function fullYearEntitlementHours(input: EntitlementInput): number {
  if (input.entitlement_override_hours != null) return round2(input.entitlement_override_hours);
  const weekly = input.contracted_hours_per_week ?? 0;
  const days = input.contracted_days_per_week;
  if (days && days > 0) {
    const entDays = Math.min(STATUTORY_WEEKS * days, STATUTORY_MAX_DAYS);
    return round2(entDays * (weekly / days));
  }
  // No day pattern known: 5.6 weeks of hours, capped at 28 x (weekly/5).
  return round2(Math.min(STATUTORY_WEEKS * weekly, STATUTORY_MAX_DAYS * (weekly / 5)));
}

function daysInclusive(a: DateTime, b: DateTime): number {
  return Math.round(b.diff(a, 'days').days) + 1;
}

/** Pro-rate an annual figure for the portion of the leave year the person is employed. */
export function proRataForYear(annualHours: number, year: LeaveYear, startDate?: string | null, endDate?: string | null): number {
  const ys = DateTime.fromISO(year.start);
  const ye = DateTime.fromISO(year.end);
  const s = startDate ? DateTime.max(ys, DateTime.fromISO(startDate)) : ys;
  const e = endDate ? DateTime.min(ye, DateTime.fromISO(endDate)) : ye;
  if (e < s) return 0;
  const fraction = daysInclusive(s, e) / daysInclusive(ys, ye);
  // Round up to the nearest quarter hour (statutory leave may not be rounded down).
  return Math.ceil(annualHours * fraction * 4) / 4;
}

/** Reg 15A: in the first year of employment, leave available accrues monthly. */
export function firstYearAccrued(annualHoursForYear: number, employmentStart: string, asOf: string, hpd: number | null): number | null {
  const start = DateTime.fromISO(employmentStart);
  const now = DateTime.fromISO(asOf);
  if (now >= start.plus({ years: 1 })) return null; // not in first year
  if (now < start) return 0;
  const monthsStarted = Math.floor(now.diff(start, 'months').months) + 1; // accrues at the start of each month
  const raw = (annualHoursForYear * Math.min(12, monthsStarted)) / 12;
  if (!hpd) return round2(raw);
  const days = raw / hpd;
  const whole = Math.floor(days);
  const frac = days - whole;
  const rounded = frac === 0 ? whole : frac <= 0.5 ? whole + 0.5 : whole + 1;
  return round2(Math.min(rounded * hpd, annualHoursForYear));
}

export function irregularAccrual(hoursWorked: number): number {
  return round2(hoursWorked * IRREGULAR_ACCRUAL_RATE);
}

export interface BalanceInput extends EntitlementInput {
  as_of: string;
  leave_year_start_month?: number;
  leave_year_start_day?: number;
  /** Approved entitlement-deducting leave hours already taken (end date < as_of). */
  taken_hours: number;
  /** Approved future leave hours. */
  booked_hours: number;
  pending_hours: number;
  adjustments_hours: number;
  /** Hours worked so far this leave year (for irregular-hours accrual). */
  hours_worked_in_year?: number;
  /** Gross pay so far this leave year in pence (for rolled-up holiday pay), if known. */
  gross_pay_pence_in_year?: number | null;
  /** Carry-over from last year still available (already capped and expiry-checked). */
  carry_over_hours?: number;
}

export interface BalanceResult {
  leave_year_start: string;
  leave_year_end: string;
  method: 'regular_pro_rata' | 'irregular_accrual_12_07' | 'override' | HolidayMethod;
  entitlement_hours: number;
  accrued_hours: number;
  remaining_hours: number;
  carry_over_hours: number;
  /** Rolled-up only: holiday pay included in wages so far (hours equivalent, and pence when pay is known). */
  rolled_up_hours: number | null;
  rolled_up_pence: number | null;
  rate: number | null;
  hours_per_day: number | null;
  notes: string[];
}

export function computeLeaveBalance(input: BalanceInput): BalanceResult {
  const year = leaveYearFor(input.as_of, input.leave_year_start_month ?? 1, input.leave_year_start_day ?? 1);
  const notes: string[] = [];
  const hpd = hoursPerDay(input);
  const carry = round2(input.carry_over_hours ?? 0);
  const rate = input.holiday_rate ?? IRREGULAR_ACCRUAL_RATE;
  const pct = `${round2(rate * 100)}%`;
  let method: BalanceResult['method'];
  let entitlement: number;
  let accrued: number;
  let rolledHours: number | null = null, rolledPence: number | null = null;

  if (input.holiday_method === 'rolled_up') {
    method = 'rolled_up';
    entitlement = 0; accrued = 0;
    rolledHours = round2((input.hours_worked_in_year ?? 0) * rate);
    rolledPence = input.gross_pay_pence_in_year != null ? Math.round(input.gross_pay_pence_in_year * rate) : null;
    notes.push(`Holiday pay is included in wages at ${pct}, so there is no allowance to book.`);
    return {
      leave_year_start: year.start, leave_year_end: year.end, method, entitlement_hours: 0, accrued_hours: 0, remaining_hours: 0, carry_over_hours: 0,
      rolled_up_hours: rolledHours, rolled_up_pence: rolledPence, rate, hours_per_day: hpd ? round2(hpd) : null, notes,
    };
  } else if (input.holiday_method === 'accrued' || (!input.holiday_method && input.entitlement_override_hours == null && isIrregular(input.employment_type))) {
    method = input.holiday_method ? 'accrued' : 'irregular_accrual_12_07';
    accrued = round2((input.hours_worked_in_year ?? 0) * rate);
    entitlement = accrued;
    notes.push(`Accrues ${pct} of hours worked (${round2(input.hours_worked_in_year ?? 0)}h worked this holiday year).`);
  } else if (!input.holiday_method && input.entitlement_override_hours != null) {
    method = 'override';
    entitlement = round2(input.entitlement_override_hours);
    accrued = entitlement;
    notes.push('Entitlement set manually (override).');
  } else {
    method = input.holiday_method ? 'fixed' : 'regular_pro_rata';
    const annual = input.holiday_method ? fixedAllowanceHours(input) : fullYearEntitlementHours(input);
    entitlement = proRataForYear(annual, year, input.employment_start_date, input.employment_end_date);
    if (entitlement !== annual) notes.push(`Pro-rated for part of the holiday year (${annual}h full year).`);
    if (input.holiday_method && input.holiday_allowance != null) notes.push(`Fixed allowance of ${input.holiday_allowance} ${input.holiday_allowance_unit ?? 'days'} a year.`);
    accrued = entitlement;
    if (input.employment_start_date) {
      const fy = firstYearAccrued(entitlement, input.employment_start_date, input.as_of, hpd);
      if (fy !== null) {
        accrued = fy;
        notes.push('First year of employment: leave accrues monthly (1/12 per month).');
      }
    }
    if ((input.contracted_days_per_week ?? 0) >= 5 && input.holiday_allowance == null) notes.push('Capped at the 28-day statutory maximum.');
  }
  if (carry) notes.push(`Includes ${carry}h carried over from last year.`);

  const remaining = round2(accrued + carry + input.adjustments_hours - input.taken_hours - input.booked_hours);
  return {
    leave_year_start: year.start,
    leave_year_end: year.end,
    method,
    entitlement_hours: round2(entitlement),
    accrued_hours: round2(accrued),
    remaining_hours: remaining,
    carry_over_hours: carry,
    rolled_up_hours: null, rolled_up_pence: null,
    rate: method === 'accrued' || method === 'irregular_accrual_12_07' ? rate : null,
    hours_per_day: hpd ? round2(hpd) : null,
    notes,
  };
}

/**
 * Carry-over into a new year: last year's unused hours, capped. After the expiry date only the part already used
 * (leave taken in the new year up to the expiry date, which uses carry-over first) is kept.
 */
export function carryOverAvailable(o: { unused_last_year: number; max_hours: number; expiry_months: number; year_start: string; as_of: string; taken_before_expiry: number }): { hours: number; expires_on: string | null; expired_hours: number } {
  const capped = round2(Math.max(0, Math.min(o.unused_last_year, o.max_hours)));
  if (!capped || !o.expiry_months) return { hours: capped, expires_on: null, expired_hours: 0 };
  const expires = DateTime.fromISO(o.year_start).plus({ months: o.expiry_months }).minus({ days: 1 }).toISODate()!;
  if (o.as_of <= expires) return { hours: capped, expires_on: expires, expired_hours: 0 };
  const used = round2(Math.min(capped, o.taken_before_expiry));
  return { hours: used, expires_on: expires, expired_hours: round2(capped - used) };
}

/** Over-allowance check for a leave request against what's left (after pending requests). */
export function allowanceCheck(o: { method: BalanceResult['method']; paid: boolean; deducts: boolean; request_hours: number; remaining_hours: number; pending_hours: number; mode: 'warn' | 'block' }):
  { ok: boolean; warning: string | null } {
  if (!o.deducts || o.method === 'rolled_up' || o.request_hours <= 0) return { ok: true, warning: null };
  const left = round2(o.remaining_hours - o.pending_hours);
  if (o.request_hours <= left + 0.001) return { ok: true, warning: null };
  const msg = `This is ${round2(o.request_hours)}h but only ${Math.max(0, left)}h of holiday is left${o.pending_hours ? ' after pending requests' : ''}.`;
  return o.mode === 'block' ? { ok: false, warning: msg } : { ok: true, warning: msg };
}

/**
 * Hours for a leave request: counts working days (ISO weekdays in `workingDays`)
 * between start and end inclusive, half days count 0.5.
 */
export function leaveRequestHours(
  startDate: string,
  endDate: string,
  opts: { startHalf?: boolean; endHalf?: boolean; workingDays?: number[]; hoursPerDay: number },
): { days: number; hours: number } {
  const working = new Set(opts.workingDays ?? [1, 2, 3, 4, 5]);
  let d = DateTime.fromISO(startDate);
  const end = DateTime.fromISO(endDate);
  let days = 0;
  while (d <= end) {
    if (working.has(d.weekday)) {
      let v = 1;
      const iso = d.toISODate();
      if (iso === startDate && opts.startHalf) v -= 0.5;
      if (iso === endDate && opts.endHalf && !(startDate === endDate && opts.startHalf)) v -= 0.5;
      days += v;
    }
    d = d.plus({ days: 1 });
  }
  return { days, hours: round2(days * opts.hoursPerDay) };
}
